import { Modal } from 'antd';
import Button from 'components/common/Button';
import {
  ROTATION_BULK_BUSY_MESSAGE,
  ROTATION_COPY_NOT_BLOCK_MESSAGE,
  ROTATION_DELETE_ACTUALS_MESSAGE,
  ROTATION_DELETE_BODY,
  ROTATION_DELETE_ERROR_MESSAGE,
  ROTATION_DELETE_LOCKED_MESSAGE,
  ROTATION_DELETE_NOTHING_MESSAGE,
  ROTATION_DELETE_OTHER_TASKS_MESSAGE,
  ROTATION_DELETE_TOO_MANY_MESSAGE,
  ROTATION_PASTE_NO_TARGETS_MESSAGE,
  ROTATION_PASTE_NOTHING_TO_COPY_MESSAGE,
  ROTATION_SAVE_ERROR_MESSAGE,
} from 'constants/messageConstants';
import {
  AREA_UNIT,
  ROTATION_CANCEL_LABEL,
  ROTATION_DELETE_CONFIRM_LABEL,
  ROTATION_DELETE_PLAN_LINE,
  ROTATION_DELETE_TITLE,
  ROTATION_MORE_COUNT,
  ROTATION_PASTE_MAX_CELLS,
  ROTATION_COPIED_LABEL,
  ROTATION_GRID_LABEL,
  ROTATION_PADDOCK_COLUMN_LABEL,
  ROTATION_PASTE_CONCURRENCY,
  ROTATION_PLANNING_SEASON_LABEL,
} from 'constants/uiConstants';
import { useDeleteInput } from 'hooks/inputs/useDeleteInput';
import type { IPaddock } from 'interfaces/IPaddock';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { RotationCellDetails } from '../../../../../redux/slices/InsightsSlice';
import { formatAreaHa } from 'pages/FarmsPage/Workspace/Insights/nutrition/nutritionFormat';
import type { TipText } from 'pages/FarmsPage/Workspace/Insights/nutrition/TipContent';
import type {
  RotationCell,
  RotationGrid as RotationGridData,
} from './computeRotationGrid';
import RotationAnchoredPanel from './RotationAnchoredPanel';
import RotationCellEditor from './RotationCellEditor';
import RotationGridCell from './RotationGridCell';
import RotationContextMenu from './RotationContextMenu';
import RotationPasteBar, {
  type RotationBulkVerb,
  type RotationPasteState,
} from './RotationPasteBar';
import {
  copyRotationSelection,
  draftRotationCell,
  planRotationPaste,
  positionsBetween,
  rotationCellKey,
  valuesForSeason,
  type GridPosition,
  type RotationClipboard,
  type RotationGridRow,
  type RotationPasteTarget,
} from './rotationPaste';
import {
  RotationSaveError,
  useRotationCellSave,
  type RotationCellValues,
} from './useRotationCellSave';
import { useSelector } from 'react-redux';
import { runWithConcurrency } from 'utils/runWithConcurrency';
import { getApiErrorMessage } from 'utils/UtilityMethods';
import { getActiveOrganisationId } from '../../../../../redux/selectors/ActiveOrganisationSelectors';

/** How long a short note ("Saved", a paste result, a copy hint) stays up. */
const NOTE_MS = 5000;

const positionKey = ({ row, col }: GridPosition) => `${row}:${col}`;
const parsePositionKey = (key: string): GridPosition => {
  const [row, col] = key.split(':').map(Number);
  return { row, col };
};

/** A plan the advisor asked to delete. */
interface DeleteTarget {
  key: string;
  position: GridPosition;
  inputId: string;
  label: string;
  hasOtherTasks: boolean;
  hasActualTasks: boolean;
}

interface PendingDelete {
  targets: DeleteTarget[];
  locked: number;
}

/** Shown by name in the delete confirm; the rest are counted. */
const DELETE_LIST_MAX = 5;

/**
 * antd zooms a modal in from the last click. A zero position makes it grow
 * from its own centre instead (null would fall back to the last click).
 */
const MODAL_FROM_CENTRE = { x: 0, y: 0 };

const saveErrorMessage = (error: unknown) =>
  error instanceof RotationSaveError ? error.message : ROTATION_SAVE_ERROR_MESSAGE;

interface RotationGridProps {
  grid: RotationGridData;
  seasons: number[];
  planningSeason: number;
  /** False makes the grid read-only (select and copy only). */
  canEdit: boolean;
  details: RotationCellDetails;
  /** The paddock records, for building a new plan. */
  paddocksById: Map<string, IPaddock>;
  lockReasonFor: (cell: RotationCell) => TipText | null;
}

/**
 * The Rotation planner grid (OP-2501), spreadsheet style.
 *
 * - One cell is active (keyboard focus): arrow keys, Home and End move it; a
 *   click selects it. Enter, F2 or a double-click opens its editor.
 * - Shift+click or Shift+arrows select a range; Ctrl/⌘+click adds or
 *   removes single cells, so a selection needn't be next to each other.
 * - Ctrl/⌘+C copies the active cell; Ctrl/⌘+V shows the paste in the grid
 *   as drafts (and skipped cells with the reason), with Apply and Cancel in
 *   a bar beside them; Apply saves it (up to ROTATION_PASTE_MAX_CELLS).
 *
 * - Empty second-crop rows can't be selected: a second crop is added in
 *   Planning.
 *
 * Cells are memoised and their handlers are stable, so moving or selecting
 * re-renders only the cells whose state changed, however large the farm.
 */
const RotationGrid: React.FC<RotationGridProps> = ({
  grid,
  seasons,
  planningSeason,
  canEdit,
  details,
  paddocksById,
  lockReasonFor,
}) => {
  const saveCell = useRotationCellSave();
  const activeOrganisationId = useSelector(getActiveOrganisationId);
  const { mutateAsync: deleteInput } = useDeleteInput({ activeOrganisationId });
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(
    null
  );
  const tableRef = useRef<HTMLTableElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<GridPosition>({ row: 0, col: 0 });
  const anchorRef = useRef<GridPosition>({ row: 0, col: 0 });
  const [selection, setSelection] = useState<Set<string>>(
    () => new Set(['0:0'])
  );
  const [editing, setEditing] = useState<GridPosition | null>(null);
  const [savingKeys, setSavingKeys] = useState<Set<string>>(() => new Set());
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [failed, setFailed] = useState<Map<string, string>>(() => new Map());
  const [clipboard, setClipboard] = useState<RotationClipboard | null>(null);
  const [paste, setPaste] = useState<RotationPasteState | null>(null);
  const [note, setNote] = useState<string | null>(null);
  // A paste preview shown in the grid: each target as it will look, and
  // why each skipped cell is skipped. Drafts stay until each one is saved.
  const [drafts, setDrafts] = useState<Map<string, RotationCell>>(
    () => new Map()
  );
  const [skips, setSkips] = useState<Map<string, string>>(() => new Map());
  // The cells the paste / delete bar sits beside.
  const [bulkPositions, setBulkPositions] = useState<GridPosition[]>([]);
  // One paste or delete saves at a time: a second one asked for meanwhile
  // gets this notice in the bar instead of replacing the first.
  const [busyNotice, setBusyNotice] = useState<string | null>(null);
  const isApplying = paste?.kind === 'applying';
  const isBusy = isApplying || savingKeys.size > 0;
  // Re-runs whatever failed last — the paste's or the delete's leftovers.
  const retryRef = useRef<(() => void) | null>(null);
  // Focus follows the active cell only after the user has moved it, so the
  // grid never pulls focus away from the tree when it first renders.
  const focusPendingRef = useRef(false);

  const rows = useMemo<RotationGridRow[]>(
    () =>
      grid.farms.flatMap((farm) =>
        farm.paddocks.flatMap((paddock) =>
          Array.from({ length: paddock.lanes }, (_, lane) => ({ paddock, lane }))
        )
      ),
    [grid]
  );

  // Kept in refs so the handlers handed to every cell never change.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const seasonsRef = useRef(seasons);
  seasonsRef.current = seasons;

  const cellAt = useCallback(({ row, col }: GridPosition): RotationCell | null => {
    const gridRow = rowsRef.current[row];
    const season = seasonsRef.current[col];
    if (!gridRow || season === null || season === undefined) return null;
    return gridRow.paddock.cells[season]?.[gridRow.lane] ?? null;
  }, []);

  /** Every cell but an empty second-crop row can be part of a selection. */
  const isSelectable = useCallback(({ row, col }: GridPosition): boolean => {
    const gridRow = rowsRef.current[row];
    const season = seasonsRef.current[col];
    if (!gridRow || season === null || season === undefined) return false;
    return gridRow.lane === 0 || !!gridRow.paddock.cells[season]?.[gridRow.lane];
  }, []);

  const selectionBetween = useCallback(
    (a: GridPosition, b: GridPosition) =>
      new Set(positionsBetween(a, b).filter(isSelectable).map(positionKey)),
    [isSelectable]
  );

  const selectOnly = useCallback(
    (position: GridPosition) => {
      anchorRef.current = position;
      setActive(position);
      setSelection(
        isSelectable(position) ? new Set([positionKey(position)]) : new Set()
      );
    },
    [isSelectable]
  );

  // A new set of rows or years starts a fresh selection inside the grid.
  const firstSeason = seasons[0];
  useEffect(() => {
    setActive((prev) => {
      const next = {
        row: Math.min(prev.row, Math.max(rows.length - 1, 0)),
        col: Math.min(prev.col, Math.max(seasons.length - 1, 0)),
      };
      anchorRef.current = next;
      setSelection(new Set([positionKey(next)]));
      return next;
    });
  }, [rows.length, seasons.length, firstSeason]);

  useLayoutEffect(() => {
    if (!focusPendingRef.current || editing) return;
    focusPendingRef.current = false;
    tableRef.current
      ?.querySelector<HTMLElement>(
        `[data-rotation-cell="${active.row}:${active.col}"]`
      )
      ?.focus();
  }, [active, editing]);

  useEffect(() => {
    if (!savedKey) return undefined;
    const timer = window.setTimeout(() => setSavedKey(null), NOTE_MS);
    return () => window.clearTimeout(timer);
  }, [savedKey]);

  useEffect(() => {
    if (!note) return undefined;
    const timer = window.setTimeout(() => setNote(null), NOTE_MS);
    return () => window.clearTimeout(timer);
  }, [note]);

  useEffect(() => {
    if (!busyNotice) return undefined;
    const timer = window.setTimeout(() => setBusyNotice(null), NOTE_MS);
    return () => window.clearTimeout(timer);
  }, [busyNotice]);

  // Saves stop if the page is refreshed or closed, so the browser asks first
  // while any are under way. (Moving elsewhere in the app lets them finish.)
  useEffect(() => {
    if (!isBusy) return undefined;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isBusy]);

  // A paste that saved everything says so briefly, then gets out of the way.
  useEffect(() => {
    if (paste?.kind !== 'done' || paste.failed > 0) return undefined;
    const timer = window.setTimeout(() => setPaste(null), NOTE_MS);
    return () => window.clearTimeout(timer);
  }, [paste]);

  const canOpen = useCallback(
    (position: GridPosition): boolean => {
      if (!canEdit) return false;
      const gridRow = rowsRef.current[position.row];
      if (!gridRow || !gridRow.paddock.zoneId) return false;
      const cell = cellAt(position);
      // A second crop in a season is added in Planning, not from the grid.
      if (!cell) return gridRow.lane === 0;
      return !lockReasonFor(cell);
    },
    [canEdit, cellAt, lockReasonFor]
  );

  const handleSelect = useCallback(
    (
      row: number,
      col: number,
      { extend, toggle }: { extend: boolean; toggle: boolean }
    ) => {
      const position = { row, col };
      if (extend) {
        setActive(position);
        setSelection(selectionBetween(anchorRef.current, position));
        return;
      }
      if (toggle) {
        anchorRef.current = position;
        setActive(position);
        if (!isSelectable(position)) return;
        setSelection((prev) => {
          const next = new Set(prev);
          const key = positionKey(position);
          if (next.has(key) && next.size > 1) next.delete(key);
          else next.add(key);
          return next;
        });
        return;
      }
      selectOnly(position);
    },
    [isSelectable, selectOnly, selectionBetween]
  );

  const open = useCallback(
    (position: GridPosition) => {
      if (!canOpen(position)) return;
      setSaveError(null);
      selectOnly(position);
      setEditing(position);
    },
    [canOpen, selectOnly]
  );

  const handleOpen = useCallback(
    (row: number, col: number) => open({ row, col }),
    [open]
  );

  const closeEditor = useCallback(() => {
    setEditing(null);
    setSaveError(null);
    focusPendingRef.current = true;
  }, []);

  const markSaving = (keys: string[], saving: boolean) =>
    setSavingKeys((prev) => {
      const next = new Set(prev);
      keys.forEach((key) => (saving ? next.add(key) : next.delete(key)));
      return next;
    });

  const handleSave = async (values: RotationCellValues) => {
    if (!editing) return;
    const gridRow = rows[editing.row];
    const season = seasons[editing.col];
    const paddock = gridRow && paddocksById.get(gridRow.paddock.paddockId);
    if (!gridRow || season === null || season === undefined || !paddock) {
      return;
    }
    const cell = gridRow.paddock.cells[season]?.[gridRow.lane] ?? null;
    const key = rotationCellKey(paddock.paddockId, season, gridRow.lane);

    setSaveError(null);
    markSaving([key], true);
    try {
      await saveCell({ paddock, season, inputId: cell?.inputId ?? null }, values);
      setSavedKey(key);
      setFailed((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
      closeEditor();
    } catch (error) {
      setSaveError(saveErrorMessage(error));
    } finally {
      markSaving([key], false);
    }
  };

  const copy = () => {
    const result = copyRotationSelection(selectedPositions(), rows, seasons);
    if ('error' in result) {
      setNote(
        result.error === 'notBlock'
          ? ROTATION_COPY_NOT_BLOCK_MESSAGE
          : ROTATION_PASTE_NOTHING_TO_COPY_MESSAGE
      );
      return;
    }
    setClipboard(result.clipboard);
    setNote(ROTATION_COPIED_LABEL(result.clipboard.label));
  };
  const copiedKeys = useMemo(
    () => new Set(clipboard?.sourceKeys ?? []),
    [clipboard]
  );

  const previewPaste = () => {
    if (!clipboard || !canEdit) return;
    if (isApplying) {
      setBusyNotice(ROTATION_BULK_BUSY_MESSAGE);
      return;
    }
    const plan = planRotationPaste({
      clipboard,
      positions: selectedPositions(),
      rows,
      seasons,
      lockReasonFor,
    });
    if (
      plan.targets.length === 0 &&
      plan.skipped.length === 0 &&
      plan.outside === 0
    ) {
      setNote(ROTATION_PASTE_NO_TARGETS_MESSAGE);
      return;
    }
    setNote(null);
    setDrafts(
      new Map(
        plan.targets.map((target) => [
          target.key,
          draftRotationCell(
            cellAt(target.position),
            target.values,
            target.season
          ),
        ])
      )
    );
    setSkips(new Map(plan.skipped.map((skip) => [skip.key, skip.reason])));
    const positions = [
      ...plan.targets.map((target) => target.position),
      ...plan.skipped.map((skip) => skip.position),
    ];
    // Everything copied fell outside the grid: the bar sits by the selection.
    setBulkPositions(positions.length > 0 ? positions : selectedPositions());
    setPaste({ kind: 'preview', plan });
  };

  const dropDraft = (key: string) =>
    setDrafts((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Map(prev);
      next.delete(key);
      return next;
    });

  /**
   * Runs one save (or delete) per item, a few at a time, showing progress in
   * the status card and marking any that fail with the reason.
   */
  const runBulk = async <T extends { key: string; position: GridPosition }>(
    verb: RotationBulkVerb,
    items: T[],
    run: (item: T) => Promise<void>,
    fallbackError: string,
    retry: (failed: T[]) => void
  ) => {
    const failures = new Map<string, string>();
    let done = 0;
    setSkips(new Map());
    setBulkPositions(items.map((item) => item.position));
    setPaste({ kind: 'applying', verb, done, total: items.length });
    markSaving(
      items.map((item) => item.key),
      true
    );
    await runWithConcurrency(items, ROTATION_PASTE_CONCURRENCY, async (item) => {
      try {
        await run(item);
      } catch (error) {
        failures.set(
          item.key,
          error instanceof RotationSaveError
            ? error.message
            : getApiErrorMessage(error, fallbackError)
        );
      } finally {
        done += 1;
        markSaving([item.key], false);
        dropDraft(item.key);
        setPaste({ kind: 'applying', verb, done, total: items.length });
      }
    });
    setFailed((prev) => {
      const next = new Map(prev);
      items.forEach((item) => next.delete(item.key));
      failures.forEach((reason, key) => next.set(key, reason));
      return next;
    });
    const failedItems = items.filter((item) => failures.has(item.key));
    retryRef.current = failedItems.length > 0 ? () => retry(failedItems) : null;
    setPaste({
      kind: 'done',
      verb,
      saved: items.length - failures.size,
      failed: failures.size,
    });
    focusPendingRef.current = true;
    setActive((prev) => ({ ...prev }));
  };

  const applyPaste = (targets: RotationPasteTarget[]): Promise<void> =>
    runBulk(
      'paste',
      targets,
      async (target) => {
        const paddock = paddocksById.get(target.paddockId);
        if (!paddock) throw new RotationSaveError(ROTATION_SAVE_ERROR_MESSAGE);
        await saveCell(
          { paddock, season: target.season, inputId: target.inputId },
          valuesForSeason(target.values, target.season)
        );
      },
      ROTATION_SAVE_ERROR_MESSAGE,
      (failedTargets) => void applyPaste(failedTargets)
    );

  const applyDelete = (targets: DeleteTarget[]): Promise<void> =>
    runBulk(
      'delete',
      targets,
      async (target) => {
        await deleteInput(target.inputId);
      },
      ROTATION_DELETE_ERROR_MESSAGE,
      (failedTargets) => void applyDelete(failedTargets)
    );

  const selectedPositions = () =>
    selection.size > 0 ? Array.from(selection, parsePositionKey) : [active];

  /** Opens the delete confirm for the selected plans; locked plans are counted, not deleted. */
  const requestDelete = () => {
    if (isApplying) {
      setBusyNotice(ROTATION_BULK_BUSY_MESSAGE);
      return;
    }
    const targets: DeleteTarget[] = [];
    let locked = 0;
    selectedPositions().forEach((position) => {
      const gridRow = rows[position.row];
      const season = seasons[position.col];
      const cell = cellAt(position);
      if (!gridRow || season === null || season === undefined || !cell) {
        return;
      }
      if (lockReasonFor(cell)) {
        locked += 1;
        return;
      }
      targets.push({
        key: rotationCellKey(gridRow.paddock.paddockId, season, gridRow.lane),
        position,
        inputId: cell.inputId,
        label: ROTATION_DELETE_PLAN_LINE(
          gridRow.paddock.paddockName,
          season,
          [cell.cropName, cell.varietyName].filter(Boolean).join(' · ')
        ),
        hasOtherTasks: cell.hasOtherTasks,
        hasActualTasks: cell.hasActualTasks,
      });
    });
    if (targets.length === 0 && locked === 0) {
      setNote(ROTATION_DELETE_NOTHING_MESSAGE);
      return;
    }
    setPendingDelete({ targets, locked });
  };

  const closeDelete = () => {
    setPendingDelete(null);
    focusPendingRef.current = true;
    setActive((prev) => ({ ...prev }));
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    const { targets } = pendingDelete;
    setPendingDelete(null);
    void applyDelete(targets);
  };

  const handleContextMenu = useCallback(
    (row: number, col: number, x: number, y: number) => {
      const position = { row, col };
      // Right-click outside the selection selects that cell first, as in a
      // spreadsheet; inside it, the menu acts on the whole selection.
      setSelection((prev) => {
        if (prev.has(positionKey(position))) return prev;
        anchorRef.current = position;
        return isSelectable(position)
          ? new Set([positionKey(position)])
          : new Set<string>();
      });
      setActive(position);
      setMenu({ x, y });
    },
    [isSelectable]
  );

  const closeMenu = useCallback(() => setMenu(null), []);

  /** How many selected plans Delete would act on, for the menu label. */
  const deletableCount = useMemo(() => {
    const positions =
      selection.size > 0 ? Array.from(selection, parsePositionKey) : [active];
    return positions.filter((position) => {
      const cell = cellAt(position);
      return cell !== null && cell !== undefined && !lockReasonFor(cell);
    }).length;
  }, [active, cellAt, lockReasonFor, selection]);

  const handleApply = () => {
    if (paste?.kind !== 'preview') return;
    void applyPaste(paste.plan.targets);
  };

  const handleRetry = () => {
    retryRef.current?.();
  };

  const handleCancelPaste = () => {
    setPaste(null);
    setDrafts(new Map());
    setSkips(new Map());
    focusPendingRef.current = true;
    setActive((prev) => ({ ...prev }));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTableElement>) => {
    // The editor's popover renders in a portal, but React still bubbles its
    // key presses here — only key presses on the grid itself count.
    if (editing || !tableRef.current?.contains(event.target as Node)) return;
    const isCommand = event.ctrlKey || event.metaKey;
    const lastRow = rows.length - 1;
    const lastCol = seasons.length - 1;
    const move = (row: number, col: number) => {
      event.preventDefault();
      focusPendingRef.current = true;
      const position = {
        row: Math.max(0, Math.min(lastRow, row)),
        col: Math.max(0, Math.min(lastCol, col)),
      };
      if (event.shiftKey) {
        setActive(position);
        setSelection(selectionBetween(anchorRef.current, position));
      } else {
        selectOnly(position);
      }
    };

    if (isCommand && event.key.toLowerCase() === 'c') {
      event.preventDefault();
      copy();
      return;
    }
    if (isCommand && event.key.toLowerCase() === 'v') {
      event.preventDefault();
      previewPaste();
      return;
    }
    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      if (canEdit) requestDelete();
      return;
    }
    switch (event.key) {
      case 'ArrowUp':
        move(active.row - 1, active.col);
        break;
      case 'ArrowDown':
        move(active.row + 1, active.col);
        break;
      case 'ArrowLeft':
        move(active.row, active.col - 1);
        break;
      case 'ArrowRight':
        move(active.row, active.col + 1);
        break;
      case 'Home':
        move(isCommand ? 0 : active.row, 0);
        break;
      case 'End':
        move(isCommand ? lastRow : active.row, lastCol);
        break;
      case 'Enter':
      case 'F2':
        event.preventDefault();
        open(active);
        break;
      case 'Escape':
        // As in a spreadsheet: drop the copy and the selection.
        if (paste?.kind === 'preview') {
          handleCancelPaste();
        } else {
          setClipboard(null);
          selectOnly(active);
        }
        break;
      default:
        break;
    }
  };

  const renderEditor = (position: GridPosition, gridRow: RotationGridRow) => {
    const season = seasons[position.col];
    const cell = gridRow.paddock.cells[season]?.[gridRow.lane] ?? null;
    const key = rotationCellKey(gridRow.paddock.paddockId, season, gridRow.lane);
    return (
      <RotationCellEditor
        key={key}
        paddockName={gridRow.paddock.paddockName}
        season={season}
        cell={cell}
        isSaving={savingKeys.has(key)}
        saveError={saveError}
        onSave={handleSave}
        onCancel={closeEditor}
      />
    );
  };

  const showSelection = selection.size > 1;
  let rowIndex = -1;
  return (
    <div className="flex flex-col gap-2">
      {paste && (
        <RotationPasteBar
          state={paste}
          containerRef={scrollRef}
          positions={bulkPositions}
          busyNotice={busyNotice}
          onApply={handleApply}
          onCancel={handleCancelPaste}
          onRetry={handleRetry}
          onDismiss={() => setPaste(null)}
        />
      )}
      {note && !paste && (
        // Short notes (copied, nothing to paste) sit by the active cell.
        <RotationAnchoredPanel containerRef={scrollRef} positions={[active]}>
          <p className="text-sm text-charcoal">{note}</p>
        </RotationAnchoredPanel>
      )}
      <div ref={scrollRef} className="max-w-full overflow-x-auto">
        <table
          ref={tableRef}
          role="grid"
          aria-label={ROTATION_GRID_LABEL}
          aria-multiselectable
          aria-rowcount={rows.length}
          // Paddock column plus five 10rem years (about 960px). Narrower than
          // that it scrolls in its own area; wider screens share out the rest.
          className="w-full min-w-[60rem] table-fixed border-collapse text-left"
          onKeyDown={handleKeyDown}
        >
          <colgroup>
            <col className="w-[10rem]" />
            {seasons.map((season) => (
              <col key={season} className="w-[10rem]" />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th
                scope="col"
                className="sticky left-0 top-0 z-20 border border-gray-bright bg-white px-2 py-2 text-sm font-semibold text-gray-dark"
              >
                {ROTATION_PADDOCK_COLUMN_LABEL}
              </th>
              {seasons.map((season) => {
                const isPlanning = season === planningSeason;
                return (
                  <th
                    key={season}
                    scope="col"
                    aria-current={isPlanning ? 'date' : undefined}
                    className={
                      isPlanning
                        ? 'sticky top-0 z-10 border border-gray-bright border-t-charcoal bg-rowHover px-2 py-2 text-sm font-bold text-charcoal'
                        : 'sticky top-0 z-10 border border-gray-bright bg-white px-2 py-2 text-sm font-semibold text-gray-dark'
                    }
                  >
                    <span className="flex items-center gap-2">
                      {season}
                      {isPlanning && (
                        <span className="truncate rounded bg-charcoal px-1.5 py-px text-sm font-semibold text-white">
                          {ROTATION_PLANNING_SEASON_LABEL}
                        </span>
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {grid.farms.map((farm) => (
              <React.Fragment key={farm.farmId}>
                <tr>
                  <th
                    scope="rowgroup"
                    colSpan={seasons.length + 1}
                    className="border border-gray-bright bg-secondary-light px-2 py-1 text-base font-semibold text-charcoal"
                  >
                    <span className="sticky left-2">
                      {farm.farmName}
                      <span className="ml-2 font-normal text-gray-dark">
                        {formatAreaHa(farm.areaHa)} {AREA_UNIT}
                      </span>
                    </span>
                  </th>
                </tr>
                {farm.paddocks.flatMap((paddock) =>
                  Array.from({ length: paddock.lanes }, (_, lane) => {
                    rowIndex += 1;
                    const row = rowIndex;
                    return (
                      <tr key={`${paddock.paddockId}:${lane}`}>
                        {lane === 0 && (
                          <th
                            scope="row"
                            rowSpan={paddock.lanes}
                            className="sticky left-0 z-[1] border border-gray-bright bg-white px-2 py-1 align-top text-base font-semibold text-charcoal"
                          >
                            <span className="flex min-w-0 items-baseline gap-2">
                              <span className="truncate">
                                {paddock.paddockName}
                              </span>
                              {paddock.areaHa !== null &&
                                paddock.areaHa !== undefined && (
                                <span className="flex-shrink-0 text-sm font-normal text-gray-muted">
                                  {formatAreaHa(paddock.areaHa)} {AREA_UNIT}
                                </span>
                              )}
                            </span>
                          </th>
                        )}
                        {seasons.map((season, col) => {
                          const key = rotationCellKey(
                            paddock.paddockId,
                            season,
                            lane
                          );
                          const draft = drafts.get(key) ?? null;
                          const cell =
                            draft ?? paddock.cells[season]?.[lane] ?? null;
                          const isEditing =
                            editing?.row === row && editing.col === col;
                          return (
                            <RotationGridCell
                              key={season}
                              row={row}
                              col={col}
                              cell={cell}
                              details={details}
                              noZone={!paddock.zoneId}
                              isSpareRow={lane > 0 && !cell}
                              isDraft={!!draft}
                              skipReason={skips.get(key) ?? null}
                              isPlanningSeason={season === planningSeason}
                              isActive={active.row === row && active.col === col}
                              isSelected={
                                showSelection &&
                                selection.has(positionKey({ row, col }))
                              }
                              isCopied={copiedKeys.has(key)}
                              lockReason={
                                cell && !draft ? lockReasonFor(cell) : null
                              }
                              isSaving={savingKeys.has(key)}
                              isSaved={savedKey === key}
                              failedReason={failed.get(key) ?? null}
                              editor={
                                isEditing
                                  ? renderEditor({ row, col }, { paddock, lane })
                                  : null
                              }
                              onCloseEditor={closeEditor}
                              onSelect={handleSelect}
                              onOpen={handleOpen}
                              onContextMenu={handleContextMenu}
                            />
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {menu && (
        <RotationContextMenu
          x={menu.x}
          y={menu.y}
          canEdit={canOpen(active)}
          canPaste={!!clipboard && canEdit}
          deleteCount={canEdit ? deletableCount : 0}
          onEdit={() => open(active)}
          onCopy={copy}
          onPaste={previewPaste}
          onDelete={requestDelete}
          onClose={closeMenu}
        />
      )}
      {pendingDelete && (
        <Modal
          open
          mousePosition={MODAL_FROM_CENTRE}
          title={ROTATION_DELETE_TITLE(pendingDelete.targets.length)}
          onCancel={closeDelete}
          footer={
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="px-3"
                onClick={closeDelete}
              >
                {ROTATION_CANCEL_LABEL}
              </Button>
              {pendingDelete.targets.length > 0 &&
                pendingDelete.targets.length <= ROTATION_PASTE_MAX_CELLS && (
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    className="px-3"
                    onClick={confirmDelete}
                  >
                    {ROTATION_DELETE_CONFIRM_LABEL}
                  </Button>
                )}
            </div>
          }
        >
          {(() => {
            const { targets, locked } = pendingDelete;
            const withOtherTasks = targets.filter((t) => t.hasOtherTasks).length;
            const withActuals = targets.filter((t) => t.hasActualTasks).length;
            const tooMany = targets.length > ROTATION_PASTE_MAX_CELLS;
            return (
              <div className="flex flex-col gap-2 text-sm text-charcoal">
                {tooMany ? (
                  <p>
                    {ROTATION_DELETE_TOO_MANY_MESSAGE(
                      ROTATION_PASTE_MAX_CELLS,
                      targets.length
                    )}
                  </p>
                ) : (
                  <>
                    {targets.length > 0 && (
                      <ul className="flex flex-col">
                        {targets.slice(0, DELETE_LIST_MAX).map((t) => (
                          <li key={t.key} className="font-semibold">
                            {t.label}
                          </li>
                        ))}
                        {targets.length > DELETE_LIST_MAX && (
                          <li>{ROTATION_MORE_COUNT(targets.length - DELETE_LIST_MAX)}</li>
                        )}
                      </ul>
                    )}
                    {targets.length > 0 && (
                      <p className="text-gray-dark">{ROTATION_DELETE_BODY}</p>
                    )}
                    {withOtherTasks > 0 && (
                      <p className="text-danger-dark">
                        {ROTATION_DELETE_OTHER_TASKS_MESSAGE(withOtherTasks)}
                      </p>
                    )}
                    {withActuals > 0 && (
                      <p className="text-danger-dark">
                        {ROTATION_DELETE_ACTUALS_MESSAGE(withActuals)}
                      </p>
                    )}
                  </>
                )}
                {locked > 0 && (
                  <p className="text-gray-dark">
                    {ROTATION_DELETE_LOCKED_MESSAGE(locked)}
                  </p>
                )}
              </div>
            );
          })()}
        </Modal>
      )}
    </div>
  );
};

export default RotationGrid;
